/**
 * React Starter Kit (https://www.reactstarterkit.com/)
 *
 * Copyright © 2014-present Kriasoft, LLC. All rights reserved.
 *
 * This source code is licensed under the MIT license found in the
 * LICENSE.txt file in the root directory of this source tree.
 */

import { createBrowserHistory } from 'history';
import sha1 from 'js-sha1';
import DataStore from './core/datastore';
import { isSessionStorageEnabled } from './core/util';

// Navigation manager, e.g. history.push('/home')
// https://github.com/mjackson/history
const history = process.env.BROWSER && createBrowserHistory();
export default history;

const _datastore = {};

const FIREFOX_WARNING_MSG =
  'Maximum FireFox history size exceeded. ' +
  'Using browser navigation, (ie. Back/Forward) will return you to the submissions page. ' +
  'Please use the on-page navigation or consider switching to another browser to avoid ' +
  'resubmitting your query.';
const MAX_FIREFOX_HISTORY = 1000000;

export class FireFoxMaxHistoryError extends Error {
  // Unfortunately javascript Errors are still a total mess
  //  and using the `name` attribute is simple and consistent
  constructor(...params) {
    super(...params);
    this.name = 'FireFoxMaxHistoryError';
    this.message = FIREFOX_WARNING_MSG;
  }
}

function isFireFox() {
  return navigator.userAgent.toLowerCase().indexOf('firefox') > -1;
}

function exceedsFireFoxMaxHistorySize(prevState, newState) {
  const completeState = JSON.stringify(Object.assign({}, prevState, newState));
  return completeState.length >= MAX_FIREFOX_HISTORY;
}

export function getSessionStorage(storageName) {
  if (isSessionStorageEnabled()) {
    const datastoreHashKey = history?.location?.state?.stateKey;
    const locState = JSON.parse(sessionStorage.getItem(storageName))?.[
      datastoreHashKey
    ];
    return locState || null;
  }
  return null;
}

function _getStateKey(name, newState) {
  const oldState = getSessionStorage(name);
  const newStoredState = Object.assign({}, oldState, newState);
  _datastore[name] = _datastore[name] || new DataStore(name);
  const jsonState = JSON.stringify(newStoredState);
  const stateKey = sha1(jsonState);
  _datastore[name].set(stateKey, newStoredState);
  return stateKey;
}

export function updateSessionStorage(storageName, updatedState) {
  if (isSessionStorageEnabled()) {
    const newStateKey = _getStateKey(storageName, updatedState);
    history.replace(history.location.pathname, { stateKey: newStateKey });
  }
}

export function geneHistoryPush(data, url = '/gene') {
  const { genes } = data;
  const queryString = genes.map(gene => gene.entrez).join('+');
  history.push(`${url}/${queryString}`);
}

export async function pushHistory(url, newState) {
  const prevState = history?.location?.state;
  if (isFireFox() && exceedsFireFoxMaxHistorySize(history, newState)) {
    throw new FireFoxMaxHistoryError();
  }
  history.push({
    pathname: url,
    state: Object.assign({}, prevState, newState),
  });
}

export async function updateHistory(updatedState, newUrl = null) {
  const prevState = history?.location?.state;
  if (isFireFox() && exceedsFireFoxMaxHistorySize(history, updatedState)) {
    throw new FireFoxMaxHistoryError();
  }
  history.replace(
    newUrl === null
      ? `${history.location.pathname}${history.location.search}`
      : newUrl,
    Object.assign({}, prevState, updatedState),
  );
}
